import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	redirect,
	useNavigate,
} from "@tanstack/react-router";
import {
	Activity,
	BarChart01,
	Briefcase02,
	Building07,
	Cube01,
	Dataflow01,
	FileSearch02,
	Laptop01,
	LayersThree01,
	Passport,
	Settings01,
	Settings04,
	Shield01,
	Ticket01,
	Users01,
} from "@untitledui/icons";
import { useEffect, useMemo } from "react";
import type { NavItemType } from "@/components/application/app-navigation/config";
import { AppToaster } from "@/components/application/notifications/toaster";
import { Button } from "@/components/base/buttons/button";
import { Shell } from "@/components/Organisms/Shell/Shell";
import { waitForConfig } from "@/config";
import { useAuth } from "@/contexts/AuthProvider";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useUserAvatarSrc } from "@/hooks/use-user-avatar-src";
import { authQueryOptions } from "@/services/auth";
import {
	type Organization,
	OrganizationStatusCode,
	organizationQueryOptions,
} from "@/services/organization";
import {
	useAppActions,
	useCurrentOrganization,
} from "@/stores/app-hooks";
import {
	getAuthState,
	syncCurrentUserToMixpanel,
} from "@/stores/auth-store";
import {
	handleMissingChunkError,
	isMissingChunkError,
} from "@/utils/reload-on-missing-chunk";
import {
	isPlaceholderOrgId,
	resolveConcreteOrgId,
	selectActiveCanViewOrganizationDetailsMemberships,
	toAdminSwitcherOrganizations,
} from "@/utils/web-org-redirect";

/**
 * Admin Layout Route
 *
 * This layout provides enhanced permission checking for all admin routes.
 * It catches permission errors at a higher level than basic authentication.
 *
 * Features:
 * - System role validation (Operations Admin or higher)
 * - Enhanced error handling for permission failures
 * - Admin-specific navigation and context
 * - Real-time permission monitoring
 */

// ============================================================================
// ROUTE DEFINITION
// ============================================================================

export const Route = createFileRoute("/_authenticated/(admin-app)/_admin")({
	/**
	 * Admin permission guard - runs before any admin route loads
	 */
	beforeLoad: async ({ location, context }) => {
		console.log("🔐 Admin Layout: Checking admin permissions...");

		// Ensure config is loaded before making API calls
		await waitForConfig();

		const authState = getAuthState();

		if (!authState.isAuthenticated || !authState.user) {
			console.log("❌ Admin Layout: User not authenticated");
			throw redirect({
				to: "/login",
				search: { redirect: location.href },
			});
		}

		const isPlatformAdminQuery = await context.queryClient.ensureQueryData(
			authQueryOptions.checkAction({
				name: "ViewAllOrganizations",
				entityType: null,
				entityId: null,
			}),
		);

		if (!isPlatformAdminQuery) {
			console.log("❌ Admin Layout: Insufficient system role permissions");
			throw redirect({ to: "/unauthorized" });
		}

		console.log("✅ Admin Layout: Permission check passed", {
			platformAdmin: isPlatformAdminQuery,
		});

		// Permissions have been validated, child routes can proceed
	},

	/**
	 * Error boundary for admin permission failures
	 */
	errorComponent: AdminErrorBoundary,

	/**
	 * Admin layout component
	 */
	component: AdminLayout,
});

/**
 * Admin Error Boundary Component
 */
function AdminErrorBoundary({
	error,
	reset,
}: {
	error: Error;
	reset: () => void;
}) {
	const navigate = useNavigate();
	const { t } = useAppTranslation();
	const isStaleChunk = isMissingChunkError(error);

	useEffect(() => {
		handleMissingChunkError(error);
	}, [error]);

	return (
		<div className="min-h-screen flex items-center justify-center bg-gray-50">
			<div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8">
				<div className="text-center">
					<div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
						<svg
							className="h-6 w-6 text-red-600"
							fill="none"
							viewBox="0 0 24 24"
							strokeWidth="1.5"
							stroke="currentColor"
							aria-hidden="true"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
							/>
						</svg>
					</div>
					<h1 className="mt-4 text-xl font-bold text-gray-900">
						{isStaleChunk
							? t("admin.error.staleChunkTitle")
							: t("admin.error.adminAccessError")}
					</h1>
					<p className="mt-2 text-sm text-gray-600">
						{isStaleChunk
							? t("admin.error.staleChunkMessage")
							: error?.message ||
								"An error occurred while checking admin permissions"}
					</p>

					<div className="mt-6 flex flex-col gap-3">
						<Button
							onClick={() => {
								if (isStaleChunk) {
									window.location.reload();
									return;
								}
								reset();
							}}
							className="w-full bg-blue-600 text-white hover:bg-blue-700"
						>
							{t("admin.error.retryAccess")}
						</Button>
						{!isStaleChunk && (
							<Button
								onClick={() => navigate({ to: "/admin/home" })}
								color="secondary"
								className="w-full"
							>
								{t("admin.error.returnToDashboard")}
							</Button>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

/**
 * Admin Layout Component
 */
function AdminLayout() {
	const auth = useAuth();
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const storedOrganization = useCurrentOrganization();
	const { setApp, setAvailableOrganizations, setOrganization } =
		useAppActions();
	const userInfoQuery = useQuery(authQueryOptions.userInfo());

	const viewAllOrgsQuery = useQuery(
		authQueryOptions.checkAction({
			name: "ViewAllOrganizations",
			entityType: null,
			entityId: null,
		}),
	);

	const isPlatformAdmin = viewAllOrgsQuery.data ?? false;

	// Get admin permission context
	const organizationList = useQuery({
		...organizationQueryOptions.all(),
		enabled: isPlatformAdmin,
	});

	const viewDetailsOrganizations = useMemo(() => {
		const memberships = selectActiveCanViewOrganizationDetailsMemberships(
			userInfoQuery.data?.organizationUserInfo ?? [],
		);
		const organizations = memberships
			.map((membership) => membership.organization)
			.filter(
				(org): org is NonNullable<typeof org> =>
					!!org && typeof org.id === "string",
			);
		return toAdminSwitcherOrganizations(organizations);
	}, [userInfoQuery.data?.organizationUserInfo]);

	const allOrganizations = useMemo<Organization[]>(() => {
		if (isPlatformAdmin) {
			return [
				{
					id: "all",
					name: t("admin.menu.allOrganizations"),
					mfaEnabled: true,
					createdBy: null,
					createdAt: null,
					createdAtOffset: null,
					updatedBy: null,
					updatedAt: null,
					updatedAtOffset: null,
					totalUserCount: 0,
					statusCode: OrganizationStatusCode.Active,
					oAuthProviderId: null,
					oAuthProvider: null,
					status: {
						code: OrganizationStatusCode.Active,
						name: "Active",
						description: "The organization is active",
					},
				},
				...(organizationList.data || []),
			];
		}
		if (!userInfoQuery.isSuccess) return [];
		return viewDetailsOrganizations;
	}, [
		isPlatformAdmin,
		organizationList.data,
		userInfoQuery.isSuccess,
		viewDetailsOrganizations,
		t,
	]);

	const firstConcreteOrgId = allOrganizations.find(
		(org) => !isPlaceholderOrgId(org.id),
	)?.id;
	const concreteOrgId = resolveConcreteOrgId({
		storeOrgId: storedOrganization?.id,
		activeOrganizationId: getAuthState().activeOrganizationId,
		userInfoCurrentOrganizationId: userInfoQuery.data?.currentOrganizationId,
		firstAvailableOrgId: firstConcreteOrgId,
		availableOrganizations: allOrganizations,
	});
	const currentOrgId =
		storedOrganization?.id === "all" &&
		allOrganizations.some((org) => org.id === "all")
			? "all"
			: (concreteOrgId ?? "all");

	const currentOrganization = allOrganizations.find(
		(org) => org.id === currentOrgId,
	);

	useEffect(() => {
		setApp("admin");
		setAvailableOrganizations(allOrganizations);
		if (currentOrganization?.id) {
			setOrganization(currentOrganization.id);
			if (!isPlaceholderOrgId(currentOrganization.id)) {
				getAuthState().setActiveOrganization(currentOrganization.id);
			} else {
				syncCurrentUserToMixpanel();
			}
		}
	}, [
		allOrganizations,
		currentOrganization?.id,
		setApp,
		setAvailableOrganizations,
		setOrganization,
	]);

	// Monitor permissions and handle runtime permission changes
	useEffect(() => {
		if (!isPlatformAdmin) {
			console.warn("⚠️ Admin Layout: User no longer has admin access");
			navigate({
				to: "/unauthorized",
				search: {
					reason: t("admin.error.adminAccessRevoked"),
				},
			});
		}
	}, [isPlatformAdmin, navigate, t]);

	// ============================================================================
	// NAVIGATION CONFIGURATION
	// ============================================================================

	const adminNavigationItems: NavItemType[] = [
		{
			label: t("admin.nav.items.home.label"),
			testId: "admin-leftnav-overview-link",
			href: "/admin/home",
			icon: BarChart01,
		},
		{
			label: t("admin.nav.items.users.label"),
			testId: "admin-leftnav-users-link",
			href: "/admin/users",
			icon: Users01,
		},
		{
			label: t("admin.nav.items.roles.label"),
			testId: "admin-leftnav-roles-link",
			href: "/admin/roles",
			icon: Passport,
		},
		{
			label: t("admin.nav.items.organizations.label"),
			testId: "admin-leftnav-organizations-link",
			href: "/admin/organizations",
			icon: Building07,
		},
		{
			label: t("admin.nav.items.configuration.label"),
			testId: "admin-leftnav-configuration-link",
			icon: Settings04,
			items: [
				{
					label: t("admin.nav.items.jobTypes.label"),
					testId: "admin-leftnav-job-types-link",
					href: "/admin/configuration/jobTypes",
					icon: Briefcase02,
				},
				{
					label: t("admin.nav.items.ticketTypes.label"),
					testId: "admin-leftnav-ticket-types-link",
					href: "/admin/configuration/ticketTypes",
					icon: Ticket01,
				},
				{
					label: t("admin.nav.items.recordTypes.label"),
					testId: "admin-leftnav-record-types-link",
					href: "/admin/configuration/recordTypes",
					icon: LayersThree01,
				},
				{
					label: t("admin.nav.items.assetTypes.label"),
					testId: "admin-leftnav-asset-types-link",
					href: "/admin/configuration/assetTypes",
					icon: Cube01,
				},
				{
					label: t("admin.nav.items.lifecycles.label"),
					testId: "admin-leftnav-lifecycles-link",
					href: "/admin/configuration/lifecycles",
					icon: Dataflow01,
				},
			],
		},
		{
			label: t("admin.nav.items.activity.label"),
			testId: "admin-leftnav-activity-link",
			icon: Activity,
			items: [
				{
					label: t("admin.nav.items.activity.logs.label"),
					testId: "admin-leftnav-activity-logs-link",
					href: "/admin/activity/logs",
					icon: FileSearch02,
				},
				{
					label: t("admin.nav.items.activity.sessions.label"),
					testId: "admin-leftnav-activity-sessions-link",
					href: "/admin/activity/sessions",
					icon: Laptop01,
				},
			],
		},
		{
			label: t("admin.nav.items.settings.label"),
			testId: "admin-leftnav-settings-link",
			href: "/admin/settings",
			icon: Settings01,
		},
	];

	const { src: userAvatarSrc } = useUserAvatarSrc(
		auth.user?.id || "",
		auth.user?.hasAvatar,
	);

	return (
		<>
			<Shell
				variant="brand"
				accountMode="portal"
				logoSrc="/images/AdminLogo.png"
				// Navigation
				items={adminNavigationItems}
				// User Info
				name={
					auth.user?.displayName || auth.user?.firstName || t("admin.user.name")
				}
				email={auth.user?.email || ""}
				onSignOut={auth.logout}
				onUserProfile={() => {
					// Navigate with profileDrawer search param (defined in _authenticated route)
					const currentSearch = new URLSearchParams(window.location.search);
					currentSearch.set("profileDrawer", "open");
					navigate({
						to: ".",
						search: Object.fromEntries(currentSearch) as Record<string, string>,
					});
				}}
				homeUrl="/admin/home"
				// Brand LeftNav — active/icon classes derived from variant in LeftNav
				activeBackgroundClass="bg-gray-100"
				iconClassName="text-gray-600"
				headerText={t("admin./.title")}
				// Organization Switcher Card
				showOrgNavCard={true}
				showOrgNavSelector={allOrganizations.length > 1}
				userAvatarSrc={userAvatarSrc || undefined}
				user1Name={
					currentOrganization?.name || t("admin.menu.selectOrganization")
				}
				orgNavTooltipTitle={
					currentOrgId === "all" ? t("admin.menu.allOrganizations") : undefined
				}
				avatar1Initials={
					currentOrganization?.name?.charAt(0) ||
					t("admin.menu.organizationInitials")
				}
				avatar1BackgroundColor="bg-[var(--Colors-Warning-600,#DC6803)]"
				avatar1Src={Building07}
				// Application Switcher Card (footer)
				showAppNavCard={true}
				showAppNavSelector={true}
				user2Name={t("admin.menu.title")}
				avatar2Initials="A"
				avatar2BackgroundColor="bg-[rgba(255,255,255,0.12)]"
				avatar2Src={Shield01}
				availableOrganizations={allOrganizations}
				currentOrganization={currentOrganization ?? allOrganizations[0]}
				disabled={allOrganizations.length === 1}
				isPlatformAdmin={isPlatformAdmin}
				mainAppNavCardOnClickHandler={() => {
					if (concreteOrgId) {
						setOrganization(concreteOrgId);
						getAuthState().setActiveOrganization(concreteOrgId);
						navigate({
							to: `/web/${concreteOrgId}/home`,
						});
					}
				}}
				adminAppNavCardOnClickHandler={() => {
					if (isPlatformAdmin) {
						navigate({
							to: "/admin/home",
						});
					}
				}}
			>
				<Outlet />
			</Shell>
			<AppToaster />
		</>
	);
}
